
import React from 'react';
import { X, GripVertical, Layers, Trash2 } from 'lucide-react';
import IconButton from './IconButton';

const GroupPanel = ({ 
  availableColumns, 
  activeGroups, 
  onAddGroup, 
  onRemoveGroup, 
  onReorderGroup,
  onClearGroups 
}) => {

  // Handlers para Drag-and-Drop da lista de colunas disponíveis
  const handleDragStartColumn = (e, columnValue) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'column', value: columnValue }));
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  // Handlers para Drag-and-Drop dos grupos ativos (reordenação)
  const handleDragStartGroup = (e, index) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'group', index }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Necessário para permitir o drop
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex = null) => {
    e.preventDefault();
    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    try {
      const data = JSON.parse(dataStr);

      if (data.type === 'column') {
        // Adicionando novo grupo vindo da lista
        onAddGroup(data.value);
      } else if (data.type === 'group') {
        // Reordenando grupos existentes
        if (targetIndex !== null) {
          onReorderGroup(data.index, targetIndex);
        }
      }
    } catch (err) {
      console.error('Erro no drop:', err);
    }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
      <div className="flex flex-col gap-4">
        
        {/* Lista de Colunas Disponíveis */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Colunas Disponíveis (Arraste para agrupar)
          </p>
          <div className="flex flex-wrap gap-2">
            {availableColumns.map((col) => (
              <div
                key={col.value}
                draggable
                onDragStart={(e) => handleDragStartColumn(e, col.value)}
                onClick={() => onAddGroup(col.value)} // Click também adiciona para acessibilidade/facilidade
                className={`
                  flex items-center gap-1 px-3 py-1.5 text-sm rounded-full cursor-grab active:cursor-grabbing border transition-colors select-none
                  ${activeGroups.includes(col.value) 
                    ? 'bg-blue-100 text-blue-700 border-blue-200 opacity-50' 
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600 shadow-sm'}
                `}
                title={col.label}
              >
                <GripVertical className="w-3 h-3 text-gray-400" />
                {col.label}
              </div>
            ))}
          </div>
        </div>

        {/* Área de Drop / Grupos Ativos */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Níveis de Agrupamento
            </p>
            {activeGroups.length > 0 && (
              <IconButton
                icon={Trash2}
                onClick={onClearGroups}
                variant="danger"
                size="sm"
                title="Limpar todos"
              />
            )}
          </div>

          <div 
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, activeGroups.length)} // Drop no final se soltar na área vazia
            className={`
              min-h-[60px] p-2 rounded-md border-2 border-dashed transition-colors flex flex-wrap items-center gap-2
              ${activeGroups.length > 0 ? 'border-gray-300 bg-white' : 'border-gray-300 bg-gray-50 justify-center'}
            `}
          >
            {activeGroups.length === 0 ? (
              <span className="text-sm text-gray-400 italic">
                Arraste colunas aqui para agrupar os dados
              </span>
            ) : (
              activeGroups.map((groupKey, index) => {
                const col = availableColumns.find(c => c.value === groupKey) || { label: groupKey };
                return (
                  <div
                    key={`${groupKey}-${index}`}
                    draggable
                    onDragStart={(e) => handleDragStartGroup(e, index)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      e.stopPropagation(); // Evita bubble para o container pai
                      handleDrop(e, index);
                    }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-full shadow-sm cursor-move select-none group animate-in fade-in zoom-in duration-200"
                  >
                    <span className="font-mono text-blue-200 text-xs mr-1">{index + 1}</span>
                    <span className="text-sm font-medium">{col.label}</span>
                    <IconButton
                      icon={X}
                      onClick={(e) => { e.stopPropagation(); onRemoveGroup(groupKey); }}
                      variant="secondary"
                      size="sm"
                      title="Remover agrupamento"
                      className="ml-1"
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default GroupPanel;
